<template>
  <div>
    <label :for="id" class="form-label">
      {{ label }}
      <span v-if="validate.includes('required') && !disabled" class="required-status">*</span>
    </label>

    <Field
      v-if="!disabled"
      v-slot="{ field, meta }"
      :name="name || id"
      :modelValue="modelValue || file"
      :rules="!disabled ? validate : ''"
    >
      <input
        type="file"
        class="form-control"
        :class="{ 'is-invalid': sizeError || (Array.isArray(errors) && errors.length) || meta?.errors?.length }"
        ref="fileInput"
        :id="id"
        :modelValue="field.value"
        :accept="accept"
        @change="handleFileChange($event)"
        :placeholder="placeholder"
        :disabled="disabled"
      />

      <div
        v-if="sizeError || (Array.isArray(errors) && errors.length) || errors.length || meta?.errors?.length"
        class="invalid-feedback" style="display: block"
      >
        {{ sizeError || (Array.isArray(errors) ? errors[0] : errors) }}
        {{ !sizeError && Array.isArray(meta.errors) ? meta.errors[0] : '' }}
      </div>
    </Field>

    <LoadingData v-if="loading" class="mt-4" />
    <div v-else-if="imagePreview || preview" class="mt-4">
      <div class="imgs-container">
        <div v-if="uploadLoading" class="spinner-container">
          <LoadingSpinner />
        </div>
        <div class="img-container">
          <img :src="imagePreview || getFileURL(preview)" alt="Img" />
          <button
            v-if="imagePreview || (preview && removeImageStatus)"
            class="delete-icon"
            @click.prevent="imagePreview ? removeImage() : removeImageStatus ? removeImageApi() : null"
          >
            <i class="mdi mdi-delete" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import { customAlert } from '@/utils/sweetalert';
import { commonUploadFile, generatePresignedUrl } from '@/services/main';
import { getFileURL } from '@/utils/helper';

const { t } = useI18n();
const emit = defineEmits(['update:modelValue']);

const uploadLoading = ref(false);
const fileInput = ref(null);
const file = ref('');
const imagePreview = ref('');

const props = defineProps({
  modelValue: { type: String, default: '' },
  id: { type: String, default: '' },
  name: { type: String, default: '' }, // for validation
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  validate: { type: [String, Object], default: '' },
  errors: { type: [String, Array], default: '' },

  maxSize: { type: [Number, String], default: 0 },
  accept: { type: String, default: '*' },
  preview: { type: String, default: '' },
  removeImageStatus: { type: Boolean, default: true },
  loading: { type: Boolean, default: false },

  uploadStatus: { type: Boolean, default: false },
  uploadUrl: { type: String, default: '' },
  uploadFilePath: { type: String, default: '' },
});

const sizeError = ref('');

const handleFileChange = (e) => {
  sizeError.value = '';
  if (e.target.files && e.target.files[0]) {
    const selectedFile = e.target.files[0];
    const maxSizeKB = Number(props.maxSize);
    if (maxSizeKB > 0 && selectedFile.size > maxSizeKB * 1024) {
      sizeError.value = t('validation.max_file_size', { max: maxSizeKB });
      fileInput.value.value = '';
      imagePreview.value = '';
      emit('update:modelValue', '');
      file.value = '';
      return;
    }

    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => (imagePreview.value = e.target.result);
      reader.readAsDataURL(selectedFile);
    } else imagePreview.value = '';

    if (props.uploadStatus) {
      uploadLoading.value = true;
      const formData = new FormData();
      formData.append('filename', e.target.files[0]?.name);
      formData.append('path', props.uploadFilePath);
      generatePresignedUrl(props.uploadUrl, formData)
        .then((res) => {
          const { uploading_url, headers } = res.data;
          commonUploadFile(uploading_url, e.target.files[0], headers)
            .then(() => {
              emit('update:modelValue', res.data.file_url);
              file.value = e.target.files[0];
              customAlert(t('main.done'), 'success');
            })
            .catch(() => {
              emit('update:modelValue', '');
              file.value = '';
              imagePreview.value = '';
            })
            .finally(() => {
              uploadLoading.value = false;
            });
        })
        .catch(() => {
          emit('update:modelValue', '');
          file.value = '';
          imagePreview.value = '';
          uploadLoading.value = false;
        });
    } else {
      emit('update:modelValue', e.target.files[0]);
      file.value = e.target.files[0];
    }
  } else {
    imagePreview.value = '';
    emit('update:modelValue', '');
    file.value = '';
  }
};

const removeImage = () => {
  fileInput.value.value = '';
  imagePreview.value = '';
  emit('update:modelValue', '');
  file.value = '';
};
const removeImageApi = () => {
  emit('removeImageApi');
};

if (props.modelValue) {
  const reader = new FileReader();
  reader.onload = (e) => (imagePreview.value = e.target.result);
  reader.readAsDataURL(props.modelValue);
}
</script>

<style scoped lang="scss">
.spinner-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgb(0 0 0 / 20%);
  z-index: 1;
}
</style>
